/**
 * Web accounts client (spec 11). Optional, like the rest of the API layer: when
 * VITE_API_URL is set, a person can create an account so their subscription and
 * progress follow them across devices instead of being pinned to this browser.
 * The teaser and on-device history keep working with no account at all.
 *
 * The session token is kept in localStorage (this is a real web app, not a
 * sandboxed artifact) and mirrored in memory. A tiny subscribe/notify store lets
 * React components re-render on sign in/out without pulling in a state library.
 */
import { useSyncExternalStore } from 'react';
import { getAnonId } from './history';

const API = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '');
const TOKEN_KEY = 'facet.token.v1';

export interface Session {
  token: string;
  user: { id: string; email: string | null; email_verified?: boolean; is_admin?: boolean };
  entitlement: { active: boolean; expiresAt?: string };
}

let current: Session | null = null;
const listeners = new Set<() => void>();
function notify(): void {
  for (const l of listeners) l();
}

function safeGet(k: string): string | null {
  try {
    return window.localStorage.getItem(k);
  } catch {
    return null;
  }
}
function safeSet(k: string, v: string): void {
  try {
    window.localStorage.setItem(k, v);
  } catch {
    /* private mode */
  }
}
function safeRemove(k: string): void {
  try {
    window.localStorage.removeItem(k);
  } catch {
    /* ignore */
  }
}

export const accountsEnabled = (): boolean => !!API;
export const getToken = (): string | null => current?.token ?? safeGet(TOKEN_KEY);
export const getSession = (): Session | null => current;
/** Authorization header for API calls, empty when signed out. */
export function authHeader(): Record<string, string> {
  const t = getToken();
  return t ? { authorization: `Bearer ${t}` } : {};
}
/** The id the API should attribute activity to: the account if signed in, else the device. */
export const effectiveUserId = (): string => current?.user.id ?? getAnonId();

function setSession(data: Session): void {
  current = data;
  safeSet(TOKEN_KEY, data.token);
  notify();
}

type Result = { ok: true } | { ok: false; message: string };

async function authRequest(path: string, email: string, password: string): Promise<Result> {
  if (!API) return { ok: false, message: 'Accounts are not available right now.' };
  try {
    const res = await fetch(`${API}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      // Pass the guest id so the account claims this browser's teaser history.
      body: JSON.stringify({ email, password, anonId: getAnonId() }),
    });
    const data = (await res.json().catch(() => ({}))) as Partial<Session> & { message?: string };
    if (!res.ok || !data.token || !data.user) {
      return { ok: false, message: data.message ?? 'Something went wrong. Please try again.' };
    }
    setSession(data as Session);
    return { ok: true };
  } catch {
    return { ok: false, message: 'Network error. Please check your connection and try again.' };
  }
}

export const signup = (email: string, password: string): Promise<Result> =>
  authRequest('/v1/auth/signup', email, password);
export const login = (email: string, password: string): Promise<Result> =>
  authRequest('/v1/auth/login', email, password);

export function logout(): void {
  current = null;
  safeRemove(TOKEN_KEY);
  notify();
}

async function authedPost(path: string, body: unknown): Promise<Result> {
  const t = getToken();
  if (!API || !t) return { ok: false, message: 'You are not signed in.' };
  try {
    const res = await fetch(`${API}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${t}` },
      body: JSON.stringify(body),
    });
    const data = (await res.json().catch(() => ({}))) as { message?: string };
    if (!res.ok) return { ok: false, message: data.message ?? 'Something went wrong. Please try again.' };
    return { ok: true };
  } catch {
    return { ok: false, message: 'Network error. Please try again.' };
  }
}

/** Change the password after reauth. The server rotates the account's token
 *  version, so every prior session (including this browser's current token) is
 *  invalidated; it returns a fresh session signed at the new version, which we
 *  adopt so this device stays signed in while other devices are logged out. */
export async function changePassword(currentPassword: string, newPassword: string): Promise<Result> {
  const t = getToken();
  if (!API || !t) return { ok: false, message: 'You are not signed in.' };
  try {
    const res = await fetch(`${API}/v1/auth/change-password`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${t}` },
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    const data = (await res.json().catch(() => ({}))) as Partial<Session> & { message?: string };
    if (!res.ok) return { ok: false, message: data.message ?? 'Something went wrong. Please try again.' };
    if (data.token && data.user) setSession(data as Session); // swap to the reissued token
    return { ok: true };
  } catch {
    return { ok: false, message: 'Network error. Please try again.' };
  }
}

/** Permanently delete the account (password-confirmed), then sign out. */
export async function deleteAccount(password: string): Promise<Result> {
  const r = await authedPost('/v1/auth/delete', { password });
  if (r.ok) logout();
  return r;
}

// ── Password reset (Wave 3.1) ────────────────────────────────────────────────
async function publicPost(path: string, body: unknown): Promise<Result> {
  if (!API) return { ok: false, message: 'Accounts are not available right now.' };
  try {
    const res = await fetch(`${API}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = (await res.json().catch(() => ({}))) as { message?: string };
    if (!res.ok) return { ok: false, message: data.message ?? 'Something went wrong. Please try again.' };
    return { ok: true };
  } catch {
    return { ok: false, message: 'Network error. Please check your connection and try again.' };
  }
}

/** Request a password-reset email. The response is intentionally generic. */
export const requestPasswordReset = (email: string): Promise<Result> =>
  publicPost('/v1/auth/request-reset', { email });

/** Redeem a reset token from the emailed link and set a new password. */
export const resetPassword = (token: string, newPassword: string): Promise<Result> =>
  publicPost('/v1/auth/reset-password', { token, newPassword });

// ── Email verification (Wave 3.1b) ───────────────────────────────────────────
/** Redeem a verification token from the emailed link. */
export const verifyEmail = (token: string): Promise<Result> => publicPost('/v1/auth/verify-email', { token });

/** Resend the verification email for the signed-in account. */
export const requestVerify = (): Promise<Result> => authedPost('/v1/auth/request-verify', {});

/** Re-hydrate the session from a stored token on load, and refresh entitlement. */
export async function restoreSession(): Promise<void> {
  const t = safeGet(TOKEN_KEY);
  if (!API || !t) return;
  try {
    const res = await fetch(`${API}/v1/auth/me`, { headers: { authorization: `Bearer ${t}` } });
    if (res.ok) {
      const data = (await res.json()) as { user: Session['user']; entitlement: Session['entitlement'] };
      current = { token: t, user: data.user, entitlement: data.entitlement };
      notify();
    } else if (res.status === 401) {
      safeRemove(TOKEN_KEY); // expired or invalid
    }
  } catch {
    /* offline: keep the token, try again next load */
  }
}

/** Pull a fresh entitlement (for example after returning from Stripe checkout). */
export async function refreshEntitlement(): Promise<boolean> {
  const t = getToken();
  if (!API || !t) return false;
  try {
    const res = await fetch(`${API}/v1/auth/me`, { headers: { authorization: `Bearer ${t}` } });
    if (!res.ok) return false;
    const data = (await res.json()) as { user: Session['user']; entitlement: Session['entitlement'] };
    current = { token: t, user: data.user, entitlement: data.entitlement };
    notify();
    return data.entitlement.active;
  } catch {
    return false;
  }
}

/** React binding: re-renders on sign in/out. */
export function useSession(): Session | null {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => current,
    () => null,
  );
}
